import { useCallback, useEffect, useState } from 'react';
import { X, BadgeCheck } from 'lucide-react';
import InvestmentSummaryForm from '../forms/InvestmentSummaryForm';

/**
 * "SEE IF I QUALIFY" attention band — rendered by PageLayout directly under
 * the shared hero slider, so it appears on every hero page. The CTA opens a
 * modal with the shared InvestmentSummaryForm (formContext 'qualify_check'):
 * Cindy runs an affordability check first, then sends the education video
 * and the example investment proposal.
 *
 * Animation lives in index.css (.qualify-*) with a prefers-reduced-motion
 * guard; the band itself is brand green with a shimmer sweep and a softly
 * pulsing white CTA.
 */
export default function QualifyStrip() {
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  // Esc closes; lock body scroll while the dialog is open
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, close]);

  return (
    <>
      <section
        aria-label="Check if you qualify to invest"
        className="qualify-strip relative overflow-hidden bg-gradient-to-r from-primary-dark via-primary to-primary-dark"
      >
        <div className="qualify-shimmer" aria-hidden="true" />
        <div className="section-container relative flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 px-4 py-4 sm:py-3.5 text-center">
          <p className="text-white font-bold text-base md:text-lg tracking-tight qualify-attention">
            Not sure if you qualify to invest?
          </p>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="qualify-cta inline-flex items-center gap-2 bg-white text-primary-dark font-extrabold text-sm md:text-base px-6 py-2.5 rounded-full shadow-md hover:shadow-lg hover:scale-105 transition-transform"
          >
            <BadgeCheck className="w-5 h-5" />
            SEE IF I QUALIFY
          </button>
        </div>
      </section>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="See if you qualify to invest"
        >
          <div
            className="absolute inset-0 bg-gray-900/70 backdrop-blur-sm"
            onClick={close}
          />
          <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="bg-white rounded-2xl">
              <div className="px-6 pt-6 md:px-8 md:pt-8 text-center">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 tracking-tight">
                  See if you qualify to invest
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  Pop in your details and we'll run a quick, free affordability
                  check on WhatsApp — no paperwork, no obligation.
                </p>
              </div>
              <InvestmentSummaryForm
                formContext="qualify_check"
                whatsappNote="Your free affordability check happens right on WhatsApp."
                successMessage="Keep an eye on your WhatsApp — we're about to run your quick affordability check, and an example of our most popular current investment options will follow."
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
